import React from "react";

export default function GrowthLoopNEPNHM() {
  return ( 
    <div className="space-y-4 text-sm text-gray-200 leading-relaxed">
        <h1 className="text-xl font-semibold text-cyan-400">
            How GrowthLoop Strengthens NEP 2020 and NHM–RBSK Through Continuous Child Development Data
        </h1>

        <p className="text-white">
            India’s child development ecosystem is guided by two major national frameworks:
            the National Education Policy (NEP) 2020 and the National Health Mission (NHM),
            under which the Rashtriya Bal Swasthya Karyakram (RBSK) focuses on early identification
            and intervention for child health and developmental challenges.
        </p>

        <h2 className="font-semibold text-cyan-200">
            National Education Policy (NEP) 2020: Foundational Learning & Holistic Development
        </h2>

        <p className="text-white">
            NEP 2020 emphasizes holistic, multidisciplinary child development, especially during
            the foundational years (ages 3–8). It highlights the importance of learning readiness,
            socio-emotional development, attention, self-regulation, and experiential learning
            over rote academic outcomes.
        </p>

        <p className="text-white">
            However, while NEP strongly defines <em>what</em> outcomes are desired, there is a
            systemic gap in <em>how</em> continuous, child-level behavioral and developmental data
            is captured, monitored, and translated into actionable support across home and school.
            Most feedback today remains episodic, subjective, or exam-centric.
        </p>

        <h2 className="font-semibold text-cyan-100">
            National Health Mission (NHM) & RBSK: Early Identification Without Longitudinal Context
        </h2>

        <p className="text-white">
            Under NHM, RBSK aims to screen children from birth to 18 years for four major categories:
            Defects at birth, Diseases, Deficiencies, and Developmental delays including behavioral
            and learning concerns. Anganwadi workers, teachers, and health professionals play a
            crucial role in early flagging and referral.
        </p>

        <p className="text-white">
            While RBSK enables early identification, it often lacks structured, longitudinal,
            non-clinical behavioral data between screenings. As a result, many children fall into
            a gray zone—showing attention, emotional, or learning difficulties that are not severe
            enough for diagnosis, yet significant enough to affect schooling and wellbeing.
        </p>

        <h2 className="font-semibold text-cyan-100">
            GrowthLoop: Closing the Data-to-Action Gap
        </h2>

        <p className="text-white">
            GrowthLoop functions as a non-clinical, continuous child development infrastructure
            that complements both NEP and NHM–RBSK by generating structured, longitudinal,
            and role-validated data across home, school, and healthcare settings.
        </p>

        <ul className="list-disc ml-5 space-y-1 [&_li]:text-white">
            <li>
                <strong>Assessment Layer:</strong> Regular parent, educator, and doctor observations
                capture attention, executive function, emotional regulation, learning readiness,
                and self-esteem indicators beyond one-time screening.
            </li>
            <li>
                <strong>Chronicles:</strong> Maintain a time-based record of attempts, routines,
                and continuity, enabling visibility into effort and progress rather than isolated outcomes.
            </li>
            <li>
                <strong>DeepThinking (RCA & Reflection):</strong> Helps caregivers and children
                understand patterns and triggers, converting behavioral concerns into solvable process
                factors rather than labels.
            </li>
            <li>
                <strong>Applause:</strong> Reinforces effort and completion, supporting NEP’s emphasis
                on confidence, motivation, and positive learning identity.
            </li>
            <li>
                <strong>Child Records:</strong> Create a portable, non-diagnostic developmental history
                that can support RBSK referrals, school transitions, and clinical consultations.
            </li>
        </ul>

        <h2 className="font-semibold text-cyan-100">
            Systemic Impact for NEP, NHM, and RBSK
        </h2>

        <p className="text-white">
            By enabling regular, low-cost, non-clinical monitoring of child behavior and learning
            readiness, GrowthLoop strengthens early intervention pathways envisioned under RBSK,
            while simultaneously supporting NEP’s goals of holistic, inclusive, and developmentally
            appropriate education.
        </p>

        <p className="text-white">
            GrowthLoop does not replace teachers, Anganwadi workers, doctors, or clinical programs.
            Instead, it acts as a connective tissue—turning everyday observations into structured
            evidence, reducing late referrals, unnecessary labeling, and preventable academic
            and emotional difficulties.
        </p>

        <p className="text-white">
            In this way, GrowthLoop transforms child development from a reactive, episodic system
            into a preventive, data-informed, and growth-oriented national capability.
        </p>
    </div>
  );
}